import React, { Fragment, ReactNode, useMemo, useState } from 'react'
import { FaChevronLeft, FaChevronRight, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { cx } from '../cx'
import { EmptyState, Spinner } from '../Feedback'
import s from './styles.module.scss'

export type SortDirection = 'asc' | 'desc'

export interface Column<Row> {
    /** Unique id; also the default accessor when `render` is omitted. */
    key: string
    header: ReactNode
    render?: (row: Row, index: number) => ReactNode
    align?: 'left' | 'right' | 'center'
    width?: number | string
    /** Enable client-side sorting; pass a function for custom comparison. */
    sortable?: boolean | ((a: Row, b: Row) => number)
    /** Value used when sorting without a custom comparator. */
    sortValue?: (row: Row) => string | number | null | undefined
    nowrap?: boolean
    /** Ellipsis overflow. */
    truncate?: boolean
    /** Hide below this width. */
    hideBelow?: number
    className?: string
}

export interface DataTableProps<Row> {
    columns: Column<Row>[]
    rows: Row[]
    rowKey: (row: Row, index: number) => string | number
    onRowClick?: (row: Row, index: number) => void
    /** Key of the row to highlight. */
    selectedKey?: string | number | null
    /** Render extra content beneath a row (only for the key(s) returned). */
    expandedKeys?: Array<string | number>
    renderExpanded?: (row: Row) => ReactNode
    /** Semantic left edge marker. */
    rowTone?: (row: Row) => 'good' | 'bad' | 'warn' | undefined
    loading?: boolean
    emptyTitle?: ReactNode
    emptyDescription?: ReactNode
    emptyAction?: ReactNode
    error?: ReactNode
    /** Sticky header inside a scrolling body. */
    maxHeight?: number
    dense?: boolean
    /** No outer border (inside a Card with padding="none"). */
    bare?: boolean
    /** Footer slot (Pagination, totals). */
    footer?: ReactNode
    /** Controlled sort; leave undefined for internal state. */
    sort?: { key: string; direction: SortDirection } | null
    onSortChange?: (sort: { key: string; direction: SortDirection } | null) => void
    className?: string
}

const defaultValue = (row: any, key: string) => row?.[key]

const compare = (a: any, b: any) => {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    if (typeof a === 'number' && typeof b === 'number') return a - b
    return String(a).localeCompare(String(b), 'en-GB', { numeric: true, sensitivity: 'base' })
}

/**
 * Typed table with hairline rows, optional sorting, selection, expansion,
 * loading and empty states. Pass `footer={<Pagination .../>}` for paging.
 */
export function DataTable<Row>({
    columns,
    rows,
    rowKey,
    onRowClick,
    selectedKey,
    expandedKeys,
    renderExpanded,
    rowTone,
    loading = false,
    emptyTitle = 'Nothing to show',
    emptyDescription,
    emptyAction,
    error,
    maxHeight,
    dense = false,
    bare = false,
    footer,
    sort: controlledSort,
    onSortChange,
    className,
}: DataTableProps<Row>) {
    const [internalSort, setInternalSort] = useState<{ key: string; direction: SortDirection } | null>(null)
    const sort = controlledSort === undefined ? internalSort : controlledSort

    const setSort = (key: string) => {
        const next: { key: string; direction: SortDirection } | null =
            sort?.key !== key ? { key, direction: 'asc' } : sort.direction === 'asc' ? { key, direction: 'desc' } : null
        if (controlledSort === undefined) setInternalSort(next)
        onSortChange?.(next)
    }

    const sortedRows = useMemo(() => {
        if (!sort || controlledSort !== undefined) return rows
        const col = columns.find((c) => c.key === sort.key)
        if (!col || !col.sortable) return rows
        const cmp = typeof col.sortable === 'function' ? col.sortable : (a: Row, b: Row) => compare((col.sortValue ?? ((r: any) => defaultValue(r, col.key)))(a), (col.sortValue ?? ((r: any) => defaultValue(r, col.key)))(b))
        const copy = [...rows].sort(cmp)
        return sort.direction === 'desc' ? copy.reverse() : copy
    }, [rows, sort, columns, controlledSort])

    const expanded = useMemo(() => new Set(expandedKeys ?? []), [expandedKeys])
    const showEmpty = !loading && !error && sortedRows.length === 0

    return (
        <div className={cx(s.wrap, bare && s.bare, className)}>
            <div className={cx(s.scroller, maxHeight && s.maxH)} style={maxHeight ? ({ ['--max-h' as any]: `${maxHeight}px` } as any) : undefined}>
                <table className={cx(s.table, dense && s.dense, maxHeight && s.sticky, loading && rows.length > 0 && s.loading)}>
                    <thead>
                        <tr>
                            {columns.map((col) => {
                                const sortable = !!col.sortable
                                const active = sort?.key === col.key
                                return (
                                    <th
                                        key={col.key}
                                        className={cx(col.align === 'right' && s.right, col.align === 'center' && s.center, sortable && s.sortable, active && s.sorted, col.className)}
                                        style={col.width ? { width: col.width } : undefined}
                                        onClick={sortable ? () => setSort(col.key) : undefined}
                                        aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                                    >
                                        {col.header}
                                        {sortable && (active ? sort!.direction === 'asc' ? <FaSortUp /> : <FaSortDown /> : <FaSort />)}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {error && (
                            <tr>
                                <td colSpan={columns.length} className={s.emptyCell}>
                                    <EmptyState bare tone="error" title="Could not load data" description={error} action={emptyAction} />
                                </td>
                            </tr>
                        )}
                        {loading && rows.length === 0 && !error && (
                            <tr>
                                <td colSpan={columns.length} className={s.emptyCell}>
                                    <Spinner block text="Loading…" />
                                </td>
                            </tr>
                        )}
                        {showEmpty && (
                            <tr>
                                <td colSpan={columns.length} className={s.emptyCell}>
                                    <EmptyState bare title={emptyTitle} description={emptyDescription} action={emptyAction} />
                                </td>
                            </tr>
                        )}
                        {!error &&
                            sortedRows.map((row, i) => {
                                const key = rowKey(row, i)
                                const isExpanded = expanded.has(key) && !!renderExpanded
                                const tone = rowTone?.(row)
                                return (
                                    <Fragment key={key}>
                                        <tr
                                            className={cx(onRowClick && s.clickable, selectedKey != null && selectedKey === key && s.selected, isExpanded && s.expanded, tone === 'good' && s.toneGood, tone === 'bad' && s.toneBad, tone === 'warn' && s.toneWarn)}
                                            onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                                        >
                                            {columns.map((col) => (
                                                <td key={col.key} className={cx(col.align === 'right' && s.right, col.align === 'center' && s.center, col.nowrap && s.nowrap, col.truncate && s.truncate, col.className)}>
                                                    {col.render ? col.render(row, i) : (defaultValue(row, col.key) as ReactNode) ?? '—'}
                                                </td>
                                            ))}
                                        </tr>
                                        {isExpanded && (
                                            <tr className={s.expandRow}>
                                                <td colSpan={columns.length}>{renderExpanded!(row)}</td>
                                            </tr>
                                        )}
                                    </Fragment>
                                )
                            })}
                    </tbody>
                </table>
            </div>
            {footer && <div className={s.foot}>{footer}</div>}
        </div>
    )
}

/** Two-line table cell: primary text with a muted secondary line. */
export const StackCell = ({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) => (
    <div className={s.stack}>
        <span className={s.stackPrimary}>{primary}</span>
        {secondary != null && secondary !== '' && <span className={s.stackSecondary}>{secondary}</span>}
    </div>
)

// ---------------------------------------------------------------- Pagination
export interface PaginationProps {
    page: number
    pageSize: number
    total: number
    onChange: (page: number) => void
    onPageSizeChange?: (size: number) => void
    pageSizeOptions?: number[]
    /** Hide the "x-y of z" text. */
    hideSummary?: boolean
    className?: string
}

const pageList = (current: number, totalPages: number): (number | '…')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | '…')[] = [1]
    const lo = Math.max(2, current - 1)
    const hi = Math.min(totalPages - 1, current + 1)
    if (lo > 2) pages.push('…')
    for (let p = lo; p <= hi; p++) pages.push(p)
    if (hi < totalPages - 1) pages.push('…')
    pages.push(totalPages)
    return pages
}

/** 1-based pagination control; pair with `DataTable footer`. */
export const Pagination = ({ page, pageSize, total, onChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100], hideSummary = false, className }: PaginationProps) => {
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)))
    const current = Math.min(Math.max(1, page), totalPages)
    const from = total === 0 ? 0 : (current - 1) * pageSize + 1
    const to = Math.min(total, current * pageSize)

    return (
        <div className={cx(s.pagination, className)}>
            {!hideSummary && (
                <span>
                    {from.toLocaleString('en-GB')}–{to.toLocaleString('en-GB')} of {total.toLocaleString('en-GB')}
                </span>
            )}
            <div className={s.pages}>
                <button type="button" className={s.pageBtn} onClick={() => onChange(current - 1)} disabled={current <= 1} aria-label="Previous page">
                    <FaChevronLeft />
                </button>
                {pageList(current, totalPages).map((p, i) =>
                    p === '…' ? (
                        <span key={`e${i}`} className={s.ellipsis}>
                            …
                        </span>
                    ) : (
                        <button key={p} type="button" className={cx(s.pageBtn, p === current && s.current)} onClick={() => onChange(p)} aria-current={p === current ? 'page' : undefined}>
                            {p}
                        </button>
                    )
                )}
                <button type="button" className={s.pageBtn} onClick={() => onChange(current + 1)} disabled={current >= totalPages} aria-label="Next page">
                    <FaChevronRight />
                </button>
            </div>
            {onPageSizeChange && (
                <select className={s.pageSize} value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} aria-label="Rows per page">
                    {pageSizeOptions.map((n) => (
                        <option key={n} value={n}>
                            {n} / page
                        </option>
                    ))}
                </select>
            )}
        </div>
    )
}

// ------------------------------------------------------------------- Toolbar
export interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Push `end` content to the right. */
    end?: ReactNode
    children?: ReactNode
}

/** Row of filters/search/actions above a table or list. */
export const Toolbar = ({ end, className, children, ...rest }: ToolbarProps) => (
    <div className={cx(s.toolbar, end && s.between, className)} {...rest}>
        <div className={s.toolbarGroup}>{children}</div>
        {end && <div className={s.toolbarGroup}>{end}</div>}
    </div>
)

export const ResultLine = ({ children, className }: { children: ReactNode; className?: string }) => <p className={cx(s.resultLine, className)}>{children}</p>
